import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const TestEnginePage = () => {
    const { quizId } = useParams();
    const navigate = useNavigate();

    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQIndex, setCurrentQIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [markedForReview, setMarkedForReview] = useState(new Set());
    const [timeLeft, setTimeLeft] = useState(0);
    const [loading, setLoading] = useState(true);
    const [paletteOpen, setPaletteOpen] = useState(false); // mobile palette drawer

    // Load Quiz
    useEffect(() => {
        api.get(`/test-zone/start-test/${quizId}`).then(res => {
            if (res.data.success) {
                setQuiz(res.data.quiz);
                setQuestions(res.data.questions);
                setTimeLeft((res.data.quiz.MaxTime || 60) * 60);
                setLoading(false);
            }
        }).catch(err => {
            console.error(err);
            alert("Failed to load test");
        });
    }, [quizId]);

    // Timer
    useEffect(() => {
        if (!timeLeft && timeLeft !== 0) return;
        if (timeLeft <= 0) return;
        const timerId = setInterval(() => {
            setTimeLeft(t => {
                if (t <= 1) { clearInterval(timerId); submitTest(); return 0; }
                return t - 1;
            });
        }, 1000);
        return () => clearInterval(timerId);
    }, [timeLeft]);

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec < 10 ? '0' + sec : sec}`;
    };

    const handleOptionSelect = (qId, optId) => setAnswers(prev => ({ ...prev, [qId]: optId }));

    const toggleMarkReview = (qId) => {
        const newSet = new Set(markedForReview);
        newSet.has(qId) ? newSet.delete(qId) : newSet.add(qId);
        setMarkedForReview(newSet);
    };

    const handleNextOrSubmit = () => {
        if (currentQIndex < questions.length - 1) {
            setCurrentQIndex(currentQIndex + 1);
            setPaletteOpen(false);
        } else {
            submitTest();
        }
    };

    const submitTest = async () => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm("Are you sure you want to finish the test?")) return;
        try {
            const res = await api.post('/test-zone/submit', {
                quizId,
                userAnswers: answers,
                timeTaken: (quiz.MaxTime * 60) - timeLeft
            });
            if (res.data.success) {
                navigate('/test-result', { state: { result: res.data.data, quiz, userAnswers: answers } });
            }
        } catch (err) { alert("Submission failed"); }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#1a0b2e] flex items-center justify-center text-white text-lg">
            Loading Test...
        </div>
    );

    const currentQ = questions[currentQIndex];
    if (!currentQ) return <div>Error loading questions</div>;
    const isMarked = markedForReview.has(currentQ.QuesId);

    // Stats for the palette summary bar
    const answeredCount  = Object.keys(answers).length;
    const reviewCount    = markedForReview.size;
    const remainingCount = questions.length - answeredCount;

    const PaletteGrid = () => (
        <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 p-4">
            {questions.map((q, idx) => {
                let cls = "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100";
                if      (answers[q.QuesId] !== undefined)     cls = "bg-green-500 text-white border-green-600";
                else if (markedForReview.has(q.QuesId))       cls = "bg-orange-400 text-white border-orange-500";
                else if (currentQIndex === idx)               cls = "bg-blue-600 text-white border-blue-700 ring-2 ring-offset-1 ring-blue-400";
                else if (currentQIndex > idx)                 cls = "bg-red-500 text-white border-red-600";
                return (
                    <button
                        key={q.QuesId}
                        onClick={() => { setCurrentQIndex(idx); setPaletteOpen(false); }}
                        className={`w-10 h-10 rounded-lg font-bold text-sm shadow-sm transition-all ${cls}`}
                    >
                        {idx + 1}
                    </button>
                );
            })}
        </div>
    );

    const PaletteLegend = () => (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-semibold text-gray-600 px-4 pb-3">
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-green-500 flex-shrink-0"></div> Answered</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded border border-gray-400 bg-white flex-shrink-0"></div> Not Answered</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-orange-400 flex-shrink-0"></div> Review</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-red-500 flex-shrink-0"></div> Visited</div>
        </div>
    );

    return (
        <div className="h-screen mt-20 bg-gray-100 flex flex-col overflow-hidden text-gray-900 font-sans">

            {/* ── Top Bar ───────────────────────────────────────────────── */}
            <div className="bg-[#1a0b2e] text-white px-3 py-3 flex justify-between items-center shadow-md z-10 gap-2">
                <div className="min-w-0 flex-1">
                    <h1 className="font-bold text-sm sm:text-lg leading-tight truncate">{quiz.title}</h1>
                    <span className="text-xs text-gray-400 hidden sm:block">{quiz.QuizCode}</span>
                </div>

                <div className="text-lg sm:text-2xl font-mono font-bold bg-white/10 px-3 py-1.5 rounded-lg border border-white/20 flex-shrink-0">
                    ⏱️ {formatTime(timeLeft)}
                </div>

                <button
                    onClick={submitTest}
                    className="bg-red-600 hover:bg-red-700 px-3 sm:px-6 py-2 rounded text-white font-bold transition-colors text-sm flex-shrink-0"
                >
                    Submit
                </button>
            </div>

            {/* ── Body ─────────────────────────────────────────────────── */}
            <div className="flex-1 flex overflow-hidden relative">

                {/* ── Question Area ──────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto bg-white">
                    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6">

                        {/* Question Header */}
                        <div className="flex justify-between items-center mb-5 border-b border-gray-200 pb-4 gap-2">
                            <span className="text-base sm:text-xl font-bold text-gray-800">
                                Q {currentQIndex + 1} <span className="text-gray-400 font-normal text-sm">/ {questions.length}</span>
                            </span>
                            <div className="flex gap-2">
                                <span className="text-xs sm:text-sm bg-green-100 text-green-800 px-2 py-1 rounded font-semibold border border-green-200">
                                    +{currentQ.Marks || 1}
                                </span>
                                <span className="text-xs sm:text-sm bg-red-100 text-red-800 px-2 py-1 rounded font-semibold border border-red-200">
                                    -0
                                </span>
                            </div>
                        </div>

                        {/* Question Text */}
                        <div
                            className="text-base sm:text-lg mb-6 sm:mb-8 text-black leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: currentQ.Question }}
                        />

                        {/* Options */}
                        <div className="space-y-3 sm:space-y-4">
                            {currentQ.options.map(opt => (
                                <label
                                    key={opt.OptionId}
                                    className={`block p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                        answers[currentQ.QuesId] === opt.OptionId
                                            ? 'border-purple-600 bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-5 h-5 sm:w-6 sm:h-6 min-w-[20px] sm:min-w-[24px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                            answers[currentQ.QuesId] === opt.OptionId
                                                ? 'bg-purple-600 border-purple-600'
                                                : 'border-gray-400 bg-white'
                                        }`}>
                                            {answers[currentQ.QuesId] === opt.OptionId && (
                                                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-white rounded-full"></div>
                                            )}
                                        </div>
                                        <div
                                            className="text-gray-900 font-medium text-sm sm:text-base"
                                            dangerouslySetInnerHTML={{ __html: opt.OptionText }}
                                        />
                                    </div>
                                    <input
                                        type="radio"
                                        name={`q-${currentQ.QuesId}`}
                                        className="hidden"
                                        onChange={() => handleOptionSelect(currentQ.QuesId, opt.OptionId)}
                                        checked={answers[currentQ.QuesId] === opt.OptionId}
                                    />
                                </label>
                            ))}
                        </div>

                        {/* Footer Controls */}
                        <div className="flex justify-between mt-8 sm:mt-12 pt-5 border-t border-gray-200 gap-2">
                            <button
                                onClick={() => toggleMarkReview(currentQ.QuesId)}
                                className={`px-3 sm:px-4 py-2 rounded-lg font-medium border-2 transition-colors text-sm ${
                                    isMarked
                                        ? 'bg-orange-100 text-orange-700 border-orange-300'
                                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {isMarked ? '⭐ Unmark' : '☆ Review'}
                            </button>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentQIndex(i => Math.max(0, i - 1))}
                                    disabled={currentQIndex === 0}
                                    className="px-4 sm:px-6 py-2 rounded-lg bg-gray-200 text-gray-700 font-bold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                    Prev
                                </button>
                                <button
                                    onClick={handleNextOrSubmit}
                                    className={`px-5 sm:px-8 py-2 rounded-lg font-bold text-white shadow-lg text-sm ${
                                        currentQIndex === questions.length - 1
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : 'bg-purple-700 hover:bg-purple-800'
                                    }`}
                                >
                                    {currentQIndex === questions.length - 1 ? 'Finish' : 'Save & Next'}
                                </button>
                            </div>
                        </div>

                        {/* Extra bottom padding so content isn't hidden behind mobile palette bar */}
                        <div className="h-6 lg:hidden" />
                    </div>
                </div>

                {/* ── Desktop Palette (lg+) ──────────────────────────────── */}
                <div className="hidden lg:flex w-72 xl:w-80 bg-gray-50 border-l border-gray-200 flex-col shadow-inner">
                    <div className="p-4 border-b border-gray-200 bg-white">
                        <h3 className="font-bold text-gray-800 mb-3">Question Palette</h3>
                        <PaletteLegend />
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <PaletteGrid />
                    </div>
                    <div className="p-3 border-t border-gray-200 bg-gray-100 text-center">
                        <div className="text-xs text-gray-500">BESST Test Engine v1.0</div>
                    </div>
                </div>
            </div>

            {/* ── Mobile Palette Drawer (bottom sheet, below lg) ─────────── */}
            <div className="lg:hidden">
                {/* Backdrop */}
                {paletteOpen && (
                    <div
                        className="fixed inset-0 bg-black/40 z-30"
                        onClick={() => setPaletteOpen(false)}
                    />
                )}

                {/* Drawer */}
                <div
                    className={`fixed bottom-0 left-0 right-0 z-40 bg-white rounded-t-2xl shadow-2xl transition-transform duration-300 ease-in-out ${
                        paletteOpen ? 'translate-y-0' : 'translate-y-full'
                    }`}
                    style={{ maxHeight: '70vh' }}
                >
                    {/* Drag handle */}
                    <div className="flex justify-center pt-3 pb-1">
                        <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
                    </div>

                    <div className="flex justify-between items-center px-4 pb-3 border-b border-gray-100">
                        <h3 className="font-bold text-gray-800">Question Palette</h3>
                        <button
                            onClick={() => setPaletteOpen(false)}
                            className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-1"
                        >
                            ×
                        </button>
                    </div>

                    <PaletteLegend />

                    <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 130px)' }}>
                        <PaletteGrid />
                    </div>
                </div>

                {/* Fixed Bottom Bar (always visible on mobile) */}
                <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 px-4 py-2 flex items-center justify-between gap-3 shadow-lg">
                    {/* Progress summary */}
                    <div className="flex gap-3 text-xs font-semibold">
                        <span className="flex items-center gap-1 text-green-700">
                            <div className="w-2.5 h-2.5 rounded-sm bg-green-500"></div>
                            {answeredCount}
                        </span>
                        <span className="flex items-center gap-1 text-orange-600">
                            <div className="w-2.5 h-2.5 rounded-sm bg-orange-400"></div>
                            {reviewCount}
                        </span>
                        <span className="flex items-center gap-1 text-gray-500">
                            <div className="w-2.5 h-2.5 rounded-sm bg-gray-300 border border-gray-400"></div>
                            {remainingCount}
                        </span>
                    </div>

                    {/* Open palette button */}
                    <button
                        onClick={() => setPaletteOpen(true)}
                        className="flex-1 py-2 bg-[#1a0b2e] text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        Q Palette ({currentQIndex + 1}/{questions.length})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TestEnginePage;